import React from 'react';
import clsx from 'clsx';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { CountUp } from '../../../components/aa';
import { EASE, Skeleton } from './primitives';

export interface Kpi {
  key: string;
  label: string;
  /** Null while it loads; `failed` says it could not be read. */
  value: number | null;
  failed?: boolean;
  /** Shown after the number, like "/19" or "%". */
  suffix?: string;
  foot: string;
  attention?: boolean;
  visual?: React.ReactNode;
  onOpen: () => void;
}

/* The headline numbers as one strip: a row of cells divided by hairlines, the
   way a dashboard reads, rather than a row of separate floating cards. */
export const KpiStrip: React.FC<{ items: Kpi[] }> = ({ items }) => {
  const reduce = useReducedMotion();
  return (
    <div className="grid grid-cols-2 gap-px overflow-hidden rounded-[14px] border border-line bg-line shadow-[0_1px_2px_rgba(24,25,29,0.04)] lg:grid-cols-5">
      {items.map((k, i) => (
        <motion.button
          key={k.key}
          type="button"
          onClick={k.onOpen}
          initial={reduce ? false : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: i * 0.05, ease: EASE }}
          className={clsx(
            'group relative flex min-h-[112px] flex-col justify-between bg-white p-4 text-left transition-colors duration-200 hover:bg-canvas focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ink/40 sm:p-5 lg:px-3.5 xl:p-5',
            i === items.length - 1 && items.length % 2 === 1 && 'col-span-2 lg:col-span-1',
          )}
        >
          <span className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-ink-dim lg:tracking-[0.04em] xl:tracking-[0.1em]">
            {k.attention && (
              <span aria-hidden className="relative flex h-2 w-2 shrink-0">
                {!reduce && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent-500 opacity-60" />}
                <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-600" />
              </span>
            )}
            <span className="truncate">{k.label}</span>
            <ArrowUpRight size={13} aria-hidden className="ml-auto shrink-0 text-ink-soft opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
          </span>
          <span className="mt-3 flex items-end justify-between gap-3">
            <span className="text-[28px] font-semibold leading-none tracking-[-0.02em] tabular-nums text-ink">
              {k.value === null && !k.failed && <Skeleton className="h-7 w-14" />}
              {k.value === null && k.failed && <span className="text-ink-soft">—</span>}
              {k.value !== null && (
                <>
                  <CountUp value={k.value} />
                  {k.suffix && <span className="text-[18px] font-medium text-ink-soft">{k.suffix}</span>}
                </>
              )}
            </span>
            {k.value !== null && k.visual && <span className="hidden shrink-0 sm:block lg:hidden xl:block">{k.visual}</span>}
          </span>
          <span className="mt-2 truncate text-[12px] text-ink-dim">{k.foot}</span>
        </motion.button>
      ))}
    </div>
  );
};
